import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { InlineEditWrapperComponent } from './core/formly/inline-edit/inline-edit-wrapper.component';
import { provideFormlyCore } from '@ngx-formly/core';
import { NGX_TIPPY_CONFIG } from 'ngx-tippy-wrapper';
import { environment } from '../environments/environment';
import { isNativeShell } from './core/utils/native-shell';
import { routes } from './app.routes';
import { getFormlyTypes } from './core/config/field-types.config';
import { CardSelectFormlyTypeComponent } from './core/formly/card-select/card-select-formly-type.component';
import { CollapsibleRadioListFormlyTypeComponent } from './core/formly/collapsible-radio-list-formly-type.component';
import { CollapsibleSectionWrapperComponent } from './core/formly/collapsible-section-wrapper.component';
import { FieldGroupSectionWrapperComponent } from './core/formly/field-group-section/field-group-section-wrapper.component';
import { FieldTypeSelectorWrapperComponent } from './core/formly/field-type-selector-wrapper.component';
import { MdeEditorFormlyTypeComponent } from './core/formly/mde-editor-formly-type.component';
import { MultiCheckboxFormlyTypeComponent } from './core/formly/multi-checkbox/multi-checkbox-formly-type.component';
import { ReportFilterListComponent } from './core/formly/report-filter-list/report-filter-list.component';
import { ReportFilterValueComponent } from './core/formly/report-filter-value/report-filter-value.component';
import { NativeSelectFormlyTypeComponent } from './core/formly/native-select/native-select-formly-type.component';
import { OptionsEditorFormlyTypeComponent } from './core/formly/options-editor-formly-type.component';
import { SliderFormlyTypeComponent } from './core/formly/slider/slider-formly-type.component';
import { TemplateContentFormlyTypeComponent } from './core/formly/template-content/template-content-formly-type.component';
import { appInitializerFactory } from './core/initializers/app.initializer';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { languageInterceptor } from './core/interceptors/language.interceptor';
import { GlobalErrorHandlerService } from './core/services/global-error-handler.service';
import { TranslationService } from './core/services/translation.service';
import { ReadonlyFieldComponent } from './pages/form-builder/fields/readonly/view/readonly-field.component';
import { ReadonlyPhotoComponent } from './pages/form-builder/fields/readonly/view/readonly-photo.component';

/**
 * Initialize translations before application starts
 * Uses global fallback first, then will reload user preference after auth
 */
export function initializeTranslations(
  translationService: TranslationService,
): () => Promise<void> {
  return (): Promise<void> => {
    const preferredLanguage = translationService.getPreferredLanguage();
    return translationService.loadLanguage(preferredLanguage);
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    provideRouter(routes),
    provideHttpClient(withFetch(), withInterceptors([authInterceptor, languageInterceptor])),
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTranslations,
      deps: [TranslationService],
      multi: true,
    },
    provideFormlyCore({
      types: [
        ...getFormlyTypes(), // User form field types (text, email, select, etc.)
        {
          // Custom field type for config editor forms
          name: 'field-type-selector',
          component: FieldTypeSelectorWrapperComponent,
        },
        {
          // Markdown editor (EasyMDE) for text-label content field
          name: 'mde-editor',
          component: MdeEditorFormlyTypeComponent,
        },
        {
          // Options editor for select/radio/checkbox-group config editor forms
          name: 'options-editor',
          component: OptionsEditorFormlyTypeComponent,
        },
        {
          // Generic collapsible accordion with responsive radio button grid
          name: 'collapsible-radio-list',
          component: CollapsibleRadioListFormlyTypeComponent,
        },
        {
          // Horizontal range slider with inline value display
          name: 'slider',
          component: SliderFormlyTypeComponent,
        },
        {
          // Grid of clickable cards; card content provided via TemplateRef in props['cardTemplate']
          name: 'card-select',
          component: CardSelectFormlyTypeComponent,
        },
        {
          // Native <select> element; preserves original value type
          name: 'native-select',
          component: NativeSelectFormlyTypeComponent,
        },
        {
          // Read-only display: renders an arbitrary ng-template passed via props['template']
          name: 'template-content',
          component: TemplateContentFormlyTypeComponent,
        },
        {
          // Read-only display for non-photo fields in submission view
          name: 'readonly-field',
          component: ReadonlyFieldComponent,
        },
        {
          // Read-only photo display with download in submission view
          name: 'readonly-photo',
          component: ReadonlyPhotoComponent,
        },
        {
          // Multi-select checkbox list; stores selected values as string[]
          name: 'multi-checkbox',
          component: MultiCheckboxFormlyTypeComponent,
        },
        {
          // Report builder filter rows (field / operator / value repeater)
          name: 'report-filter-list',
          component: ReportFilterListComponent,
        },
        {
          // Type-aware value editor for a report filter row (checklist / select /
          // typed input depending on the selected field + operator)
          name: 'report-filter-value',
          component: ReportFilterValueComponent,
        },
      ],
      wrappers: [
        {
          name: 'collapsible-section',
          component: CollapsibleSectionWrapperComponent,
        },
        {
          name: 'field-group-section',
          component: FieldGroupSectionWrapperComponent,
        },
        {
          name: 'inline-edit',
          component: InlineEditWrapperComponent,
        },
      ],
    }),
    provideServiceWorker(environment.serviceWorkerScriptUrl, {
      // Never register inside a Capacitor shell: iOS WKWebView has no service
      // workers under capacitor://, and bundled assets are already offline.
      enabled: environment.production && !isNativeShell(),
      // Aggressive registration - register immediately on app start
      registrationStrategy: 'registerImmediately',
    }),
    {
      provide: NGX_TIPPY_CONFIG,
      useValue: {
        placement: 'top',
        arrow: true,
        theme: 'light-border',
      },
    },
  ],
};
